import React, { useState, useRef, useEffect } from 'react';
import { User, Mail, Phone, MapPin, Building, CreditCard, Briefcase, CalendarCheck, Lock, Edit2, ChevronDown, ChevronUp, Shield, Wallet, GraduationCap, Users } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { buildProfileUpdatePayload, createProfileFormState, displayValue, formatBoolean, formatCurrency, formatProfileDate, normalizeUserProfile, validateProfileForm, sectionHasAtLeastOneFilledField } from '../../utils/userProfile';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const [profileUser, setProfileUser] = useState(normalizeUserProfile(user || {}));
  const [isPasswordSectionOpen, setIsPasswordSectionOpen] = useState(false);
  const [isProfileEditOpen, setIsProfileEditOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [fetchingProfile, setFetchingProfile] = useState(false);
  const profileEditRef = useRef(null);
  
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [profileData, setProfileData] = useState(createProfileFormState(user || {}));

  useEffect(() => {
    const normalized = normalizeUserProfile(user || {});
    setProfileUser(normalized);
    setProfileData(createProfileFormState(normalized));
  }, [user]);

  useEffect(() => {
    const fetchEmployeeProfile = async () => {
      try {
        setFetchingProfile(true);
        const response = await api.get('/employee/me');
        const normalized = normalizeUserProfile(response.data?.user || {});
        setProfileUser(normalized);
        updateUser(normalized);
      } catch (error) {
        console.error('Profile fetch error:', error);
      } finally {
        setFetchingProfile(false);
      }
    };

    fetchEmployeeProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleProfileChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProfileData({
      ...profileData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
      toast.error('All password fields are required');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (formData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await api.put('/employee/change-password', {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      });

      toast.success('Password updated successfully');
      
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setIsPasswordSectionOpen(false);
    } catch (error) {
      console.error('Password update error:', error);
      toast.error(error.response?.data?.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async (section) => {
    if (section?.fields && !sectionHasAtLeastOneFilledField(section.fields, profileData)) {
      toast.error(`Please fill at least one field in ${section.title} before saving.`);
      return;
    }

    const validationError = validateProfileForm(profileData);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setProfileLoading(true);
    try {
      const payload = buildProfileUpdatePayload(profileData);

      const response = await api.put('/employee/update-profile', payload);
      const normalized = normalizeUserProfile(response.data?.user || response.data?.employee || payload);

      setProfileUser((prev) => normalizeUserProfile({ ...prev, ...normalized }));
      setProfileData(createProfileFormState(normalized));
      updateUser(normalized);

      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Profile update error:', error);
      const data = error.response?.data;
      const msg = data?.message || 'Failed to update profile';
      toast.error(msg);
    } finally {
      setProfileLoading(false);
    }
  };

  const profileFields = [
    { icon: User, label: 'Name', value: displayValue(profileUser?.name), color: 'bg-blue-50 text-blue-600' },
    { icon: CreditCard, label: 'Employee ID', value: displayValue(profileUser?.employeeId), color: 'bg-green-50 text-green-600' },
    { icon: Building, label: 'Organization', value: displayValue(profileUser?.organization), color: 'bg-purple-50 text-purple-600' },
    { icon: Briefcase, label: 'Job Title', value: displayValue(profileUser?.jobTitle), color: 'bg-indigo-50 text-indigo-600' },
    { icon: Mail, label: 'Email', value: displayValue(profileUser?.email), color: 'bg-orange-50 text-orange-600' },
    { icon: Phone, label: 'Mobile Number', value: displayValue(profileUser?.mobileNumber), color: 'bg-cyan-50 text-cyan-600' },
    { icon: CalendarCheck, label: 'Joining Date', value: formatProfileDate(profileUser?.joinDate), color: 'bg-teal-50 text-teal-600' },
    { icon: MapPin, label: 'Present Address', value: displayValue(profileUser?.presentAddress || profileUser?.address), color: 'bg-rose-50 text-rose-600', fullWidth: true }
  ];

  const detailSections = [
    {
      title: 'Personal Details',
      icon: User,
      fields: [
        ['Date of Birth', formatProfileDate(profileUser?.dateOfBirth)],
        ['Gender', displayValue(profileUser?.gender)],
        ['Marital Status', displayValue(profileUser?.maritalStatus)],
        ['Alternate Number', displayValue(profileUser?.alternateNumber)],
        ['Emergency Name', displayValue(profileUser?.emergencyName)],
        ['Emergency Relation', displayValue(profileUser?.emergencyRelation)],
        ['Emergency Contact', displayValue(profileUser?.emergencyContactNumber)],
        ['Blood Group', displayValue(profileUser?.bloodGroup)],
        ['Physical Challenged', formatBoolean(profileUser?.physicalChallenged)],
        ['City', displayValue(profileUser?.city)],
        ['State', displayValue(profileUser?.state)],
        ['Pin Code', displayValue(profileUser?.pinCode)],
        ['Permanent Address', displayValue(profileUser?.permanentAddress)]
      ]
    },
    {
      title: 'KYC',
      icon: Shield,
      fields: [
        ['PAN Number', displayValue(profileUser?.panNumber)],
        ['Aadhaar Number', displayValue(profileUser?.aadhaarNumber)],
        ['Passport Number', displayValue(profileUser?.passportNumber)],
        ['PF Details', displayValue(profileUser?.pfDetails)],
        ['Medical Insurance', displayValue(profileUser?.medicalInsurance)]
      ]
    },
    {
      title: 'CTC (monthly)',
      icon: Wallet,
      fields: [
        ['Total CTC (₹ / month)', formatCurrency(profileUser?.payScale)],
        ['Basic salary (₹ / month)', formatCurrency(profileUser?.ctcBasic)],
        ['HRA (₹ / month)', formatCurrency(profileUser?.ctcHRA)],
        ['Special allowance (₹ / month)', formatCurrency(profileUser?.ctcSpecialAllowance)],
        ['Travel allowance (₹ / month)', formatCurrency(profileUser?.ctcTravelAllowance)],
        ['IT allowance (₹ / month)', formatCurrency(profileUser?.ctcITAllowance)]
      ]
    },
    {
      title: 'Education',
      icon: GraduationCap,
      fields: [
        ['School Name', displayValue(profileUser?.schoolName)],
        ['School Board', displayValue(profileUser?.schoolBoard)],
        ['School Graduation Year', displayValue(profileUser?.schoolYearOfGraduation)],
        ['School Percentage/CGPA', displayValue(profileUser?.schoolPercentageOrCGPA)],
        ['University Name', displayValue(profileUser?.universityName)],
        ['University Board', displayValue(profileUser?.universityBoard)],
        ['University Graduation Year', displayValue(profileUser?.universityYearOfGraduation)],
        ['University Percentage/CGPA', displayValue(profileUser?.universityPercentageOrCGPA)],
        ['Post Graduation Name', displayValue(profileUser?.postGraduationName)],
        ['Post Graduation Board', displayValue(profileUser?.postGraduationBoard)],
        ['Post Graduation Year', displayValue(profileUser?.postGraduationYearOfGraduation)],
        ['Post Graduation Percentage/CGPA', displayValue(profileUser?.postGraduationPercentageOrCGPA)],
        ['Highest Education Category', displayValue(profileUser?.highestEducationCategory)],
        ['Highest Education Institution', displayValue(profileUser?.highestEducationInstitutionName)],
        ['Highest Education Board', displayValue(profileUser?.highestEducationBoard)],
        ['Highest Education Year', displayValue(profileUser?.highestEducationYearOfPassing)],
        ['Highest Education Percentage/CGPA', displayValue(profileUser?.highestEducationPercentageOrCGPA)]
      ]
    },
    {
      title: 'References',
      icon: Users,
      fields: [
        ['Reference 1 Name', displayValue(profileUser?.reference1Name)],
        ['Reference 1 Relationship', displayValue(profileUser?.reference1Relationship)],
        ['Reference 1 Mobile', displayValue(profileUser?.reference1Mobile)],
        ['Reference 1 Address', displayValue(profileUser?.reference1Address)],
        ['Reference 2 Name', displayValue(profileUser?.reference2Name)],
        ['Reference 2 Relationship', displayValue(profileUser?.reference2Relationship)],
        ['Reference 2 Mobile', displayValue(profileUser?.reference2Mobile)],
        ['Reference 2 Address', displayValue(profileUser?.reference2Address)]
      ]
    },
    {
      title: 'Previous Employment',
      icon: Briefcase,
      fields: [
        ['Total Experience', displayValue(profileUser?.totalExperience)],
        ['Previous Company', displayValue(profileUser?.previousCompanyName)],
        ['Previous Company Location', displayValue(profileUser?.previousCompanyLocation)],
        ['Previous Job Title', displayValue(profileUser?.previousJobTitle)],
        ['Previous Supervisor', displayValue(profileUser?.previousSupervisorName)],
        ['Previous Supervisor Contact', displayValue(profileUser?.previousSupervisorContact)],
        ['Previous Start Date', formatProfileDate(profileUser?.previousStartDate)],
        ['Previous End Date', formatProfileDate(profileUser?.previousEndDate)],
        ['Reason For Leaving', displayValue(profileUser?.reasonForLeaving)],
        ['Relieving Letter Submitted', formatBoolean(profileUser?.relievingLetterSubmitted)]
      ]
    }
  ];

  const handleEditClick = () => {
    setIsProfileEditOpen(!isProfileEditOpen);
    if (!isProfileEditOpen) {
      setTimeout(() => {
        profileEditRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  const editSections = [
    {
      title: 'Basic Information',
      fields: [
        { name: 'name', label: 'Full Name', type: 'text', required: true },
        { name: 'jobTitle', label: 'Job Title', type: 'text' },
        { name: 'dateOfBirth', label: 'Date of Birth', type: 'date' },
        {
          name: 'gender',
          label: 'Gender',
          type: 'select',
          options: [
            { label: 'Select', value: '' },
            { label: 'Male', value: 'male' },
            { label: 'Female', value: 'female' },
            { label: 'Other', value: 'other' },
          ],
        },
        {
          name: 'maritalStatus',
          label: 'Marital Status',
          type: 'select',
          options: [
            { label: 'Select', value: '' },
            { label: 'Single', value: 'single' },
            { label: 'Married', value: 'married' },
          ],
        },
        { name: 'bloodGroup', label: 'Blood Group', type: 'text' },
        { name: 'physicalChallenged', label: 'Physical Challenged', type: 'checkbox' },
      ],
    },
    {
      title: 'Contact Information',
      fields: [
        { name: 'email', label: 'Email Address', type: 'email', required: true },
        { name: 'mobileNumber', label: 'Mobile Number', type: 'text', required: true },
        { name: 'alternateNumber', label: 'Alternate Number', type: 'text' },
        { name: 'emergencyName', label: 'Emergency Name', type: 'text' },
        { name: 'emergencyRelation', label: 'Emergency Relation', type: 'text' },
        { name: 'emergencyContactNumber', label: 'Emergency Contact Number', type: 'text' },
        { name: 'presentAddress', label: 'Present Address', type: 'textarea', required: true },
        { name: 'permanentAddress', label: 'Permanent Address', type: 'textarea' },
        { name: 'city', label: 'City', type: 'text' },
        { name: 'state', label: 'State', type: 'text' },
        { name: 'pinCode', label: 'Pin Code', type: 'text' },
      ],
    },
    {
      title: 'Identity and KYC',
      fields: [
        { name: 'panNumber', label: 'PAN Number', type: 'text' },
        { name: 'aadhaarNumber', label: 'Aadhaar Number', type: 'text' },
        { name: 'passportNumber', label: 'Passport Number', type: 'text' },
        { name: 'pfDetails', label: 'PF Details', type: 'text' },
        { name: 'medicalInsurance', label: 'Medical Insurance', type: 'text' },
      ],
    },
    {
      title: 'Education',
      fields: [
        { name: 'schoolName', label: 'School Name', type: 'text' },
        { name: 'schoolBoard', label: 'School Board', type: 'text' },
        { name: 'schoolYearOfGraduation', label: 'School Year Of Graduation', type: 'number' },
        { name: 'schoolPercentageOrCGPA', label: 'School Percentage Or CGPA', type: 'text' },
        { name: 'universityName', label: 'University Name', type: 'text' },
        { name: 'universityBoard', label: 'University Board', type: 'text' },
        { name: 'universityYearOfGraduation', label: 'University Year Of Graduation', type: 'number' },
        { name: 'universityPercentageOrCGPA', label: 'University Percentage Or CGPA', type: 'text' },
        { name: 'postGraduationName', label: 'Post Graduation Name', type: 'text' },
        { name: 'postGraduationBoard', label: 'Post Graduation Board', type: 'text' },
        { name: 'postGraduationYearOfGraduation', label: 'Post Graduation Year Of Graduation', type: 'number' },
        { name: 'postGraduationPercentageOrCGPA', label: 'Post Graduation Percentage Or CGPA', type: 'text' },
        { name: 'highestEducationCategory', label: 'Highest Education Category', type: 'text' },
        { name: 'highestEducationInstitutionName', label: 'Highest Education Institution Name', type: 'text' },
        { name: 'highestEducationBoard', label: 'Highest Education Board', type: 'text' },
        { name: 'highestEducationYearOfPassing', label: 'Highest Education Year Of Passing', type: 'number' },
        { name: 'highestEducationPercentageOrCGPA', label: 'Highest Education Percentage Or CGPA', type: 'text' },
      ],
    },
    {
      title: 'References',
      fields: [
        { name: 'reference1Name', label: 'Reference 1 Name', type: 'text' },
        { name: 'reference1Address', label: 'Reference 1 Address', type: 'textarea' },
        { name: 'reference1Mobile', label: 'Reference 1 Mobile', type: 'text' },
        { name: 'reference1Relationship', label: 'Reference 1 Relationship', type: 'text' },
        { name: 'reference2Name', label: 'Reference 2 Name', type: 'text' },
        { name: 'reference2Address', label: 'Reference 2 Address', type: 'textarea' },
        { name: 'reference2Mobile', label: 'Reference 2 Mobile', type: 'text' },
        { name: 'reference2Relationship', label: 'Reference 2 Relationship', type: 'text' },
      ],
    },
    {
      title: 'Previous Employment',
      fields: [
        { name: 'totalExperience', label: 'Total Experience', type: 'text' },
        { name: 'previousCompanyName', label: 'Previous Company Name', type: 'text' },
        { name: 'previousCompanyLocation', label: 'Previous Company Location', type: 'text' },
        { name: 'previousJobTitle', label: 'Previous Job Title', type: 'text' },
        { name: 'previousSupervisorName', label: 'Previous Supervisor Name', type: 'text' },
        { name: 'previousSupervisorContact', label: 'Previous Supervisor Contact', type: 'text' },
        { name: 'previousStartDate', label: 'Previous Start Date', type: 'date' },
        { name: 'previousEndDate', label: 'Previous End Date', type: 'date' },
        { name: 'reasonForLeaving', label: 'Reason For Leaving', type: 'textarea' },
        { name: 'relievingLetterSubmitted', label: 'Relieving Letter Submitted', type: 'checkbox' },
      ],
    },
  ];

  const renderEditableField = (field) => {
    if (field.type === 'checkbox') {
      return (
        <label className="flex items-center justify-between border border-gray-200 rounded-lg px-4 py-3">
          <span className="text-sm font-medium text-gray-700">{field.label}</span>
          <input
            type="checkbox"
            name={field.name}
            checked={Boolean(profileData[field.name])}
            onChange={handleProfileChange}
            disabled={profileLoading}
            className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
          />
        </label>
      );
    }

    if (field.type === 'select') {
      return (
        <>
          <label htmlFor={field.name} className="block text-sm font-medium text-gray-700 mb-2">{field.label}</label>
          <select
            id={field.name}
            name={field.name}
            value={profileData[field.name] || ''}
            onChange={handleProfileChange}
            disabled={profileLoading}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          >
            {field.options.map((option) => (
              <option key={`${field.name}-${option.value}`} value={option.value}>{option.label}</option>
            ))}
          </select>
        </>
      );
    }

    if (field.type === 'textarea') {
      return (
        <>
          <label htmlFor={field.name} className="block text-sm font-medium text-gray-700 mb-2">{field.label}</label>
          <textarea
            id={field.name}
            name={field.name}
            value={profileData[field.name] || ''}
            onChange={handleProfileChange}
            rows="2"
            disabled={profileLoading}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
          />
        </>
      );
    }

    return (
      <>
        <label htmlFor={field.name} className="block text-sm font-medium text-gray-700 mb-2">{field.label}</label>
        <input
          type={field.type}
          id={field.name}
          name={field.name}
          value={profileData[field.name] || ''}
          onChange={handleProfileChange}
          disabled={profileLoading}
          required={Boolean(field.required)}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
        />
      </>
    );
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
        <p className="text-gray-600">View and manage your profile information</p>
        {fetchingProfile && <p className="text-xs text-gray-500 mt-1">Refreshing latest profile data...</p>}
      </div>

      {/* Profile Information Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold -ml-2 text-gray-900">Profile Information</h2>
          <button
            onClick={handleEditClick}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
          >
            <Edit2 className="h-4 w-4" />
            Edit Profile
          </button>
        </div>
        
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {profileFields.map((field, index) => {
              const Icon = field.icon;
              return (
                <div 
                  key={index} 
                  className={`flex items-start gap-3 p-4 bg-gray-50 rounded-lg ${field.fullWidth ? 'md:col-span-2' : ''}`}
                >
                  <div className={`p-2 rounded-lg ${field.color}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{field.label}</p>
                    <p className="text-sm font-semibold text-gray-900 mt-1 break-words">{field.value}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {detailSections.map((section) => {
        const SectionIcon = section.icon;
        return (
          <div key={section.title} className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-2">
              <SectionIcon className="h-5 w-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">{section.title}</h2>
            </div>
            <div className="p-4">
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                {section.fields.map(([label, value]) => (
                  <div key={`${section.title}-${label}`} className="bg-gray-50 rounded-lg p-3">
                    <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</dt>
                    <dd className="text-sm font-semibold text-gray-900 mt-1 break-words">{value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        );
      })}

      {/* Update Profile Section */}
      <div ref={profileEditRef} className="bg-white rounded-lg shadow-sm border border-gray-200">
        <button
          onClick={handleEditClick}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Edit2 className="h-5 w-5 text-gray-600" />
            <span className="text-base font-medium text-gray-900">Update Profile Information</span>
          </div>
          {isProfileEditOpen ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </button>

        <div
            className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
              isProfileEditOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
            }`}
          >
        <div className="overflow-hidden">
          <div className="px-4 py-6 bg-gray-50 border-t border-gray-200">
            <form onSubmit={(e) => e.preventDefault()} className="space-y-5">
              {editSections.map((section) => (
                <div key={section.title} className="bg-white border border-gray-200 rounded-lg">
                  <div className="px-4 py-3 border-b border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-900">{section.title}</h3>
                  </div>
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {section.fields.map((field) => (
                      <div
                        key={field.name}
                        className={field.type === 'textarea' ? 'md:col-span-2' : ''}
                      >
                        {renderEditableField(field)}
                      </div>
                    ))}
                  </div>
                  <div className="px-4 py-3 border-t border-gray-200 flex justify-end">
                    <button
                      type="button"
                      onClick={() => saveProfile(section)}
                      disabled={profileLoading}
                      className={`px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all ${
                        profileLoading ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {profileLoading ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              ))}

              <div className="bg-white border border-gray-200 rounded-lg">
                <div className="px-4 py-3 border-b border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-900">CTC — monthly (read only)</h3>
                </div>
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div className="flex justify-between"><span className="text-gray-600">Total CTC (₹ / month)</span><span className="font-semibold text-gray-900">{formatCurrency(profileUser.payScale)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Basic salary (₹ / month)</span><span className="font-semibold text-gray-900">{formatCurrency(profileUser.ctcBasic)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">HRA (₹ / month)</span><span className="font-semibold text-gray-900">{formatCurrency(profileUser.ctcHRA)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Special allowance (₹ / month)</span><span className="font-semibold text-gray-900">{formatCurrency(profileUser.ctcSpecialAllowance)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Travel allowance (₹ / month)</span><span className="font-semibold text-gray-900">{formatCurrency(profileUser.ctcTravelAllowance)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">IT allowance (₹ / month)</span><span className="font-semibold text-gray-900">{formatCurrency(profileUser.ctcITAllowance)}</span></div>
                </div>
              </div>
            </form>
          </div>
          </div>
        </div>
      </div>

      {/* Change Password Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <button
          onClick={() => setIsPasswordSectionOpen(!isPasswordSectionOpen)}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Lock className="h-5 w-5 text-gray-600" />
            <span className="text-base font-medium text-gray-900">Change Password</span>
          </div>
          {isPasswordSectionOpen ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </button>

        <div
          className={`transition-all duration-300 ease-in-out overflow-hidden ${
            isPasswordSectionOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="px-4 py-6 bg-gray-50 border-t border-gray-200">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  Current Password
                </label>
                <input
                  type="password"
                  id="currentPassword"
                  name="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Enter current password"
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  New Password
                </label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Enter new password"
                  disabled={loading}
                />
                <p className="mt-1.5 text-xs text-gray-500">Must be at least 6 characters</p>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Confirm new password"
                  disabled={loading}
                />
              </div>

              <div className="flex justify-end pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className={`px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all ${
                    loading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {loading ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;